import moment from 'moment-timezone';
import { StringService } from '../string';
import { MediaItem } from '../../view/search-list/types';

export class DateService {
  /**
   * Converts a date to a specific timezone.
   * @param date The date to be converted. It can be a string or a Date object.
   * @param timezone The target timezone for the conversion.
   * It should be a string representing a valid IANA timezone.
   *
   * For a list of all valid IANA timezones, you can use `moment.tz.names()`.
   *
   * @returns A string representing the date converted to the target timezone.
   *
   * @throws An error if the provided timezone is not a valid IANA timezone.
   */
  static convertDateToTimeZone(date: string | Date, timezone: string): string {
    const momentInstance = moment(date).tz(timezone);
    return momentInstance.format();
  }
}

export const DateFormattingService = {
  dateTranslationKeyDefaults: {
    dateDay: 'D',
    dateMonth: 'MMMM',
    dateYear: 'YYYY',
    dateNumeric: 'M/D/YYYY',
    dateFull: 'MMMM DD, YYYY',
    dateEvents: 'MMMM DD, YYYY [at]',
    fallback: 'MMMM DD, YYYY',
  },
  months: [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ],

  formatDate(date: string | Date, dateFormat: string, translations: Record<string, string>): string {
    if (typeof date === 'string' && date.length === 0) {
      // in case we don't have a date, return an empty string.
      // This is to prevent that "Invalid date" is returned instead of an empty string.
      // This problem has been introduced when porting this service from
      // the old component library. See EXTCWP-952 for more information.
      return '';
    }
    const parsedDate = typeof date !== 'string' ? date : this.getParsedDate(date);
    const momentInstance = moment(parsedDate);

    const parsedDateFormat = translations[dateFormat] ?? dateFormat;

    let formatedDate;

    if (!parsedDateFormat || typeof parsedDateFormat !== 'string') {
      formatedDate = momentInstance.format(this.dateTranslationKeyDefaults.fallback);
    }

    if (Object.keys(this.dateTranslationKeyDefaults).includes(parsedDateFormat)) {
      formatedDate = momentInstance.format(this.dateTranslationKeyDefaults[parsedDateFormat]);
    }

    if (!formatedDate) {
      formatedDate = momentInstance.format(parsedDateFormat);
    }
    return this.translateMonth(formatedDate, translations);
  },

  translateMonth(formatedDate: string, translations: Record<string, string>): string {
    const usedMonth = this.months.filter((month: string) => formatedDate.includes(StringService.capitalize(month)))[0];

    if (!usedMonth) {
      return formatedDate;
    }

    const translatedMonth = translations[usedMonth] ?? StringService.capitalize(usedMonth);

    return formatedDate.replaceAll(StringService.capitalize(usedMonth), translatedMonth);
  },

  getDateTimeValue(date: string): string {
    const d: Date = this.getParsedDate(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  },

  getParsedDate(date: string): Date {
    return new Date(date?.replace('Z', ''));
  },

  convertUTCtoTimeZone(date: string | Date, timezone: string) {
    const momentInstance = moment(date).tz(timezone);
    return momentInstance.format();
  },

  formatMediaItem(mediaItem: MediaItem, translations: Record<string, string>): string {
    const releaseDateTime = DateFormattingService.convertDateToTimeZone(
      mediaItem.ReleaseDateTime as string | Date,
      'Europe/Berlin',
    );
    const formattedReleaseDate = DateFormattingService.formatDate(
      releaseDateTime as string | Date,
      'dateFull',
      translations,
    );
    return formattedReleaseDate;
  },

  /**
   * Converts a date to a specific timezone.
   * @param date The date to be converted. It can be a string or a Date object.
   * @param timezone The target timezone for the conversion.
   * It should be a string representing a valid IANA timezone.

   * For a list of all valid IANA timezones, you can use `moment.tz.names()`.
   *
   * @returns A string representing the date converted to the target timezone.
   *
   * @throws An error if the provided timezone is not a valid IANA timezone.
   */
  convertDateToTimeZone(date: string | Date, timezone: string) {
    const momentInstance = moment(date).tz(timezone);
    return momentInstance.format();
  },
};
